import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { StatCard } from '@/components/StatCard';
import { computeAppHealth } from '@/lib/dashboardCompute';
import { capacityTone } from '@/lib/capacity';
import { cn } from '@/lib/utils';
import type { Instance } from '@/types/instance';
import type { EndpointWithResources } from '@/types/endpoint';
import type { App } from '@/types/app';

export interface StatsRowProps {
  instances?: Instance[];
  endpoints?: EndpointWithResources[];
  apps?: App[];
  /** running is omitted when the sample is truncated by pagination. */
  containers?: { total: number; running?: number };
  deployments24h?: number;
  capacityPercent?: number;
}

const DASH = '—';
const LINK_CLASS =
  'block rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

const TONE_CLASS = {
  ok: '',
  warning: 'text-status-warning',
  danger: 'text-status-danger',
} as const;

/** Row of headline fleet metrics, each linking to its detail page. */
export function StatsRow({ instances, endpoints, apps, containers, deployments24h, capacityPercent }: StatsRowProps) {
  const instanceOnline = instances?.filter((i) => i.is_active && i.last_sync_status !== 'failed').length;
  const endpointUp = endpoints?.filter((e) => e.status === 'up').length;
  const appRunning = apps ? computeAppHealth(apps).running : undefined;

  const instanceValue = instances ? `${instanceOnline}/${instances.length}` : DASH;
  const endpointValue = endpoints ? `${endpointUp}/${endpoints.length}` : DASH;
  const appValue = apps ? `${appRunning}/${apps.length}` : DASH;
  const containerValue = containers
    ? containers.running === undefined
      ? `${containers.total}`
      : `${containers.running}/${containers.total}`
    : DASH;

  const hasCapacity = capacityPercent !== undefined && Number.isFinite(capacityPercent);
  const capacityValue: ReactNode = hasCapacity ? (
    <span className={cn(TONE_CLASS[capacityTone(capacityPercent)])}>{capacityPercent}%</span>
  ) : (
    DASH
  );

  const instanceLabel = instances ? `Instances: ${instanceOnline} of ${instances.length} online` : `Instances: ${DASH}`;
  const endpointLabel = endpoints ? `Endpoints: ${endpointUp} of ${endpoints.length} up` : `Endpoints: ${DASH}`;
  const appHint =
    deployments24h !== undefined
      ? `, ${deployments24h} ${deployments24h === 1 ? 'deployment' : 'deployments'} in 24h`
      : '';
  const appLabel = apps ? `Apps: ${appRunning} of ${apps.length} running${appHint}` : `Apps: ${DASH}${appHint}`;
  const containerLabel = containers
    ? containers.running === undefined
      ? `Containers: ${containers.total} total`
      : `Containers: ${containers.running} of ${containers.total} running`
    : `Containers: ${DASH}`;
  const capacityLabel = hasCapacity ? `Fleet capacity: ${capacityPercent}% reserved` : `Fleet capacity: ${DASH}`;

  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
      <Link to="/instances" className={LINK_CLASS} aria-label={instanceLabel}>
        <StatCard label="Instances" value={instanceValue} />
      </Link>
      <Link to="/resources/endpoints" className={LINK_CLASS} aria-label={endpointLabel}>
        <StatCard label="Endpoints" value={endpointValue} />
      </Link>
      <Link to="/apps" className={LINK_CLASS} aria-label={appLabel}>
        <StatCard
          label="Apps"
          value={appValue}
          hint={
            deployments24h !== undefined
              ? `${deployments24h} ${deployments24h === 1 ? 'deployment' : 'deployments'} in 24h`
              : undefined
          }
        />
      </Link>
      <Link to="/resources/containers" className={LINK_CLASS} aria-label={containerLabel}>
        <StatCard label="Containers" value={containerValue} />
      </Link>
      <Link
        to="/resources/endpoints"
        className={cn(LINK_CLASS, 'col-span-2 lg:col-span-1')}
        aria-label={capacityLabel}
      >
        <StatCard label="Fleet capacity" value={capacityValue} hint={hasCapacity ? 'reserved' : undefined} />
      </Link>
    </div>
  );
}
